/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Wine,
  Users,
  Grid3X3,
  CreditCard,
  BellRing,
  DoorOpen,
  ShoppingCart,
  Check,
  X,
  AlertTriangle,
  QrCode,
} from 'lucide-react';
import PrepPanel from './PrepPanel';
import NotificationBell from '../components/NotificationBell';
import QuickLinksModal from '../components/QuickLinksModal';
import ServiceRequestListener from '../components/ServiceRequestListener';
import StaffHeaderMenu from '../components/StaffHeaderMenu';
import ShiftHandoverModal from '../components/ShiftHandoverModal';
import PostSettleTablePrompt from '../components/PostSettleTablePrompt';
import StaleTabsAlert from '../components/StaleTabsAlert';
import { tableLabel } from '../lib/tableLabel';

/**
 * BarPanel — the bartender's all-in-one POS shell.
 *
 * Owner request (May 30 2026): "Let's add a few things on bar panel — i
 * want to use this panel also for taking orders on the bar, table
 * management, processing payments and customer service."
 *
 * Layout: 5 tabs (Prep Queue · Take Order · Tables · Payments · Service)
 * with a shared sticky header. The Prep Queue tab re-uses the existing
 * `PrepPanel type=bar` in **embedded** mode (no nested header). The other
 * tabs use the same patterns as the manager's Live Orders / Tables pages
 * + the floor staff's Serving panel — so a bartender doesn't have to bounce
 * between routes during a service. The ServiceRequestListener is mounted
 * once at this shell level so the audio alert + floating tray still work
 * regardless of which tab is open.
 */

const TAB_KEYS = ['queue', 'take-order', 'tables', 'payments', 'service'];

// Static tab styling map — hoisted to module scope so we don't allocate
// the same five-row array + classnames on every render of BarPanel.
// Filtered per-render via the small `useMemo` below against the role's
// `visibleTabKeys` set.
const TAB_STYLES = [
  { key: 'queue',      activeBg: 'data-[state=active]:bg-blue-accent/15 data-[state=active]:text-blue-accent data-[state=active]:border-blue-accent/50',     badgeCls: 'bg-blue-accent/20 text-blue-accent border-blue-accent/30' },
  { key: 'take-order', activeBg: 'data-[state=active]:bg-gold/15 data-[state=active]:text-gold data-[state=active]:border-gold/50',                          badgeCls: 'bg-gold/20 text-gold border-gold/30' },
  { key: 'tables',     activeBg: 'data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 data-[state=active]:border-emerald-500/50',     badgeCls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  { key: 'payments',   activeBg: 'data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300 data-[state=active]:border-amber-500/50',           badgeCls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  { key: 'service',    activeBg: 'data-[state=active]:bg-red-500/15 data-[state=active]:text-red-300 data-[state=active]:border-red-500/50',                 badgeCls: 'bg-red-500/20 text-red-300 border-red-500/40 animate-pulse' },
];

export default function BarPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    try {
      const v = localStorage.getItem('majnu:barPanelTab');
      return TAB_KEYS.includes(v) ? v : 'queue';
    } catch { return 'queue'; }
  });
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  // Shift handover modal — opens when a till supervisor / manager /
  // director clicks Sign Out from the header menu. Lets them review the
  // shift's settled-orders, payment breakdown, discounts, service-bells
  // resolved and any tabs still open before they log out. Bartenders
  // skip this (no FOH responsibilities to hand over).
  const [shiftHandoverOpen, setShiftHandoverOpen] = useState(false);
  const handleLogoutClick = () => {
    if (user?.role === 'bartender') {
      logout();
    } else {
      setShiftHandoverOpen(true);
    }
  };
  // After a tab settles, prompt whether to free the table or keep it
  // occupied (owner request May 31 2026).
  const [postSettlePromptTable, setPostSettlePromptTable] = useState(null);

  const onTabChange = (v) => {
    setTab(v);
    try { localStorage.setItem('majnu:barPanelTab', v); } catch { /* sandboxed */ }
  };

  // Shared state — fetched at the shell level so each tab gets fresh data
  // without each one polling separately and stampeding the API.
  const [tables, setTables] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [serviceReqs, setServiceReqs] = useState([]);

  const fetchTables = useCallback(async () => {
    try {
      const { data } = await api.get('/tables/overview');
      setTables(Array.isArray(data) ? data.sort((a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true })) : []);
    } catch { /* polled, will retry */ }
  }, []);

  const fetchActiveOrders = useCallback(async () => {
    try {
      const { data } = await api.get('/orders?status=active');
      setActiveOrders(Array.isArray(data) ? data : []);
    } catch { /* retry next tick */ }
  }, []);

  const fetchServiceReqs = useCallback(async () => {
    try {
      const { data } = await api.get('/service-requests/active');
      setServiceReqs(Array.isArray(data) ? data : []);
    } catch { /* retry next tick */ }
  }, []);

  useEffect(() => {
    fetchTables(); fetchActiveOrders(); fetchServiceReqs();
    const i1 = setInterval(fetchTables, 8000);
    const i2 = setInterval(fetchActiveOrders, 8000);
    const i3 = setInterval(fetchServiceReqs, 5000);
    return () => { clearInterval(i1); clearInterval(i2); clearInterval(i3); };
  }, [fetchTables, fetchActiveOrders, fetchServiceReqs]);

  // ---- Counts for the tab pills ----
  const availCount = tables.filter(t => t.status === 'available').length;
  const occCount = tables.filter(t => t.status === 'occupied').length;
  const cleaningCount = tables.filter(t => t.status === 'cleaning').length;
  const resvCount = tables.filter(t => t.status === 'reserved').length;
  const pendingPayments = activeOrders.filter(o =>
    o.payment_status !== 'paid' && o.payment_status !== 'refunded' && o.order_status !== 'cancelled'
  );
  const tabPayments = pendingPayments.filter(o => o.payment_method === 'tab');
  const directPayments = pendingPayments.filter(o => o.payment_method !== 'tab');

  // Tab metadata for the redesigned nav (June 2026 — owner directive:
  // "navigation is really confusing"). Each entry drives the big-pill
  // tab row + the breadcrumb subtitle so staff always know exactly
  // where they are and what's on the screen.
  const TABS_META = {
    queue: {
      label: 'Prep Queue',
      sub: 'Drinks waiting to be made',
      icon: Wine,
      tone: 'blue-accent',
      countLabel: null,
    },
    'take-order': {
      label: 'Take Order',
      sub: 'Build an order for a table',
      icon: ShoppingCart,
      tone: 'gold',
      countLabel: null,
    },
    tables: {
      label: 'Tables',
      sub: `${availCount} available · ${occCount} occupied${cleaningCount ? ` · ${cleaningCount} cleaning` : ''}`,
      icon: Grid3X3,
      tone: 'emerald-500',
      countLabel: `${availCount}/${tables.length}`,
    },
    payments: {
      label: 'Payments',
      sub: pendingPayments.length
        ? `${pendingPayments.length} unpaid · ${tabPayments.length} on tab · ${directPayments.length} direct`
        : 'All clear — no unpaid orders',
      icon: CreditCard,
      tone: 'amber-500',
      countLabel: pendingPayments.length ? String(pendingPayments.length) : null,
    },
    service: {
      label: 'Service',
      sub: serviceReqs.length ? `${serviceReqs.length} guest ${serviceReqs.length === 1 ? 'is' : 'are'} ringing the bell` : 'No service requests right now',
      icon: BellRing,
      tone: 'red-500',
      countLabel: serviceReqs.length ? String(serviceReqs.length) : null,
    },
  };
  const activeMeta = TABS_META[tab] || TABS_META.queue;

  // Feb 2026 workflow split: bartender's view is strictly drinks-prep
  // only. Till supervisor / manager / director get the full FOH POS.
  // Everything else (tab logic, payments, tables, service) is gated on
  // `isFullPanel`. We also drive the header title off this flag so a
  // bartender sees a clean "Bar Panel" and the till sees "Till Panel".
  const isBartenderOnly = user?.role === 'bartender';
  const isFullPanel = !isBartenderOnly;
  const panelTitle = isBartenderOnly ? 'Bar Panel' : 'Till Panel';
  // If a bartender ever lands on a non-queue tab (e.g. stored localStorage
  // from a former till session), bounce them back to the prep queue.
  React.useEffect(() => {
    if (isBartenderOnly && tab !== 'queue') {
      onTabChange('queue');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBartenderOnly, tab]);

  // Tabs the bartender can see — limited to Prep Queue. The rest live
  // on the Till Panel and the bartender doesn't need to see them.
  const visibleTabKeys = isBartenderOnly
    ? ['queue']
    : ['queue', 'take-order', 'tables', 'payments', 'service'];
  // Resolve the styled tab list once per role flip rather than per
  // render — the array of 5 styled rows is otherwise re-allocated +
  // re-filtered on every state tick (poll, prep refresh, count update).
  const visibleTabs = React.useMemo(
    () => TAB_STYLES.filter(t => visibleTabKeys.includes(t.key)),
    // visibleTabKeys is a fresh array per render but its contents are
    // role-derived; key on the JSON to capture meaningful changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleTabKeys.join(',')]
  );

  return (
    <div className="min-h-screen bg-bg-primary">
      <ServiceRequestListener />

      {/* Shared sticky header — redesigned June 2026 for clarity:
          - Big "Bar Panel" title + breadcrumb showing the active tab
          - External navigation (Live Orders / Serving Panel) clearly
            separated on the right with explicit "Open in new view" cue */}
      <header className="sticky top-0 z-30 glass-header px-3 py-2.5 sm:px-4 sm:py-3 flex items-center justify-between gap-3 flex-wrap border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-sm bg-blue-accent/10 flex items-center justify-center shrink-0">
            <Wine className="w-5 h-5 text-blue-accent" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-serif text-white truncate leading-none" data-testid="bar-panel-title">{panelTitle}</h1>
              <span className="text-zinc-600">›</span>
              <span className={`text-base sm:text-lg font-serif leading-none text-${activeMeta.tone}`} data-testid="bar-panel-current-section">
                {activeMeta.label}
              </span>
            </div>
            <p className="text-[10px] sm:text-xs text-zinc-500 font-sans mt-1 truncate" data-testid="bar-panel-subtitle">
              {activeMeta.sub} · {user?.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end shrink-0">
          {/* External navigation — visually separated as "open a different
              screen" so staff don't confuse these with the bar-panel tabs.
              Hidden for bartenders (drinks-only role). */}
          {isFullPanel && (
            <>
              <div className="hidden sm:flex items-center gap-1.5 mr-1 pr-2 border-r border-white/10">
                <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 font-sans">Jump to</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/serving')}
                className="text-zinc-300 hover:text-white hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-500/40 h-9 px-2.5 sm:px-3 rounded-sm flex items-center gap-1.5 font-sans text-[11px] sm:text-xs uppercase tracking-[0.15em]"
                data-testid="bar-goto-serving"
                title="Open Serving Panel — waiter view of all tables"
              >
                <Users className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Serving Panel</span>
                <span className="md:hidden">Serve</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard/orders')}
                className="text-zinc-300 hover:text-white hover:bg-gold/15 border border-white/10 hover:border-gold/40 h-9 px-2.5 sm:px-3 rounded-sm flex items-center gap-1.5 font-sans text-[11px] sm:text-xs uppercase tracking-[0.15em]"
                data-testid="bar-goto-orders"
                title="Open Live Orders Dashboard — full split-payments + Square flow"
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Live Orders</span>
                <span className="md:hidden">Orders</span>
              </Button>
            </>
          )}
          <NotificationBell />
          <StaffHeaderMenu
            onOpenQuickLinks={() => setQuickLinksOpen(true)}
            onLogout={handleLogoutClick}
          />
        </div>
      </header>

      <QuickLinksModal open={quickLinksOpen} onClose={() => setQuickLinksOpen(false)} />
      <ShiftHandoverModal
        open={shiftHandoverOpen}
        onClose={() => setShiftHandoverOpen(false)}
        onConfirmLogout={() => { setShiftHandoverOpen(false); logout(); }}
      />

      {/* Tabs — redesigned June 2026: big descriptive pills with the
          icon, label, and live count. The active tab gets a coloured
          underline + filled background so staff can see at a glance
          which screen they're on. Order matches the daily workflow:
          PREP → TAKE ORDER → TABLES → PAYMENTS → SERVICE. */}
      <Tabs value={tab} onValueChange={onTabChange} className="w-full">
        <TabsList
          className="sticky top-[68px] z-20 w-full justify-start rounded-none border-b border-white/10 bg-bg-primary/95 backdrop-blur-md h-auto p-2 gap-1.5 overflow-x-auto"
          data-testid="bar-panel-tabs"
        >
          {visibleTabs.map(({ key, activeBg, badgeCls }) => {
            const meta = TABS_META[key];
            const Icon = meta.icon;
            const hasUrgentCount = (key === 'payments' && pendingPayments.length > 0) || (key === 'service' && serviceReqs.length > 0);
            return (
              <TabsTrigger
                key={key}
                value={key}
                data-testid={`bar-tab-${key}`}
                className={`shrink-0 flex items-center gap-2 text-zinc-400 font-sans text-[12px] sm:text-sm uppercase tracking-[0.12em] h-11 px-3.5 sm:px-4 rounded-sm border border-transparent hover:bg-white/[0.04] ${activeBg}`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-semibold">{meta.label}</span>
                {meta.countLabel && (
                  <Badge className={`ml-0.5 font-mono text-[10px] px-1.5 border ${hasUrgentCount ? badgeCls : 'bg-white/5 text-zinc-300 border-white/10'}`} data-testid={`bar-tab-${key}-count`}>
                    {meta.countLabel}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* ---------- 1. PREP QUEUE ---------- */}
        <TabsContent value="queue" className="m-0">
          <PrepPanel type="bar" title="Bar" embedded />
        </TabsContent>

        {/* Tabs below are TILL-ONLY — bartenders never see them. The
            visibleTabKeys filter above already hides the triggers, but
            we guard the content render too in case anyone deep-links
            via `?tab=payments` from a stale URL. */}
        {isFullPanel && (
          <>
            {/* ---------- 2. TAKE ORDER ---------- */}
            <TabsContent value="take-order" className="m-0 p-4 sm:p-6">
              <BarTakeOrderTab tables={tables} />
            </TabsContent>

            {/* ---------- 3. TABLES ---------- */}
            <TabsContent value="tables" className="m-0 p-4 sm:p-6">
              <BarTablesTab
                tables={tables}
                avail={availCount} occ={occCount} resv={resvCount} cleaning={cleaningCount}
                refresh={fetchTables}
                userRole={user?.role}
                onTabSettled={(table) => setPostSettlePromptTable(table)}
              />
            </TabsContent>

            {/* ---------- 4. PAYMENTS ---------- */}
            <TabsContent value="payments" className="m-0 p-4 sm:p-6">
              <BarPaymentsTab
                tabPayments={tabPayments}
                directPayments={directPayments}
                refresh={fetchActiveOrders}
                navigate={navigate}
                onTabSettled={(table) => setPostSettlePromptTable(table)}
              />
            </TabsContent>

            {/* ---------- 5. SERVICE ---------- */}
            <TabsContent value="service" className="m-0 p-4 sm:p-6">
              <BarServiceTab requests={serviceReqs} refresh={fetchServiceReqs} />
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Post-settle "Mark Available / Keep Occupied" prompt — shared
          across the Payments tab (rendered at shell level so it lives
          above the tab content regardless of which tab is open). */}
      <PostSettleTablePrompt
        open={!!postSettlePromptTable}
        table={postSettlePromptTable}
        onClose={() => setPostSettlePromptTable(null)}
        onResolved={() => { fetchTables(); fetchActiveOrders(); }}
      />
    </div>
  );
}

/* ============================================================
   TAKE ORDER TAB
   Pick a table → renders the customer ordering view (the same
   `/table/{qr}?staff=1` page customers use, in staff-driven mode)
   INLINE via an iframe inside the Take Order tab. The Bar Panel
   header + tabs stay visible up top so the bartender can still
   flip to Prep Queue / Service mid-order without losing the cart.
   ============================================================ */
function BarTakeOrderTab({ tables }) {
  const [picked, setPicked] = useState('');
  const [activeQr, setActiveQr] = useState('');

  const startOrder = () => {
    if (!picked) { toast.error('Pick a table first'); return; }
    setActiveQr(picked);
  };

  const closeOrderFrame = () => {
    setActiveQr('');
    setPicked('');
  };

  // ----- ACTIVE ORDER VIEW (iframe) -----
  if (activeQr) {
    const pickedTable = tables.find(t => t.qr_code === activeQr);
    return (
      <section data-testid="bar-take-order-active">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="text-gold font-serif text-2xl font-semibold">{pickedTable ? tableLabel(pickedTable.number) : activeQr}</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">Ordering as staff · attaches to customer's tab</span>
          </div>
          <Button
            onClick={closeOrderFrame}
            variant="ghost"
            className="h-9 text-zinc-300 hover:text-white hover:bg-white/10 border border-white/10 rounded-sm font-sans text-xs uppercase tracking-[0.15em]"
            data-testid="bar-take-order-close"
            title="Close — go back to table picker"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </div>
        {/* Same-origin iframe — no sandbox restrictions needed. Min height
            keeps the cart + sticky place-order bar comfortably visible
            without the bartender having to scroll the iframe AND the page. */}
        <div className="rounded-sm overflow-hidden border border-white/10 bg-bg-secondary" data-testid="bar-take-order-iframe-wrap">
          <iframe
            key={activeQr}
            src={`/table/${activeQr}?staff=1`}
            title={`Take order — ${pickedTable ? tableLabel(pickedTable.number) : activeQr}`}
            className="w-full bg-bg-primary"
            style={{ height: 'calc(100vh - 200px)', minHeight: '600px', border: '0' }}
            data-testid="bar-take-order-iframe"
            allow="clipboard-read; clipboard-write; payment"
          />
        </div>
      </section>
    );
  }

  // ----- TABLE PICKER -----
  return (
    <section data-testid="bar-take-order-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-sm sm:text-base font-serif text-white uppercase tracking-[0.18em]">Take Order — Pick Customer's Table</h2>
          <p className="text-[10px] sm:text-xs text-zinc-500 font-sans mt-1">
            Order view opens INLINE below in staff mode — payment defaults to Cash, attaches to the customer's open tab when one exists. Close it any time to return here.
          </p>
        </div>
        <Button
          onClick={startOrder}
          disabled={!picked}
          className="h-10 bg-gold/20 text-gold hover:bg-gold/30 border border-gold/40 disabled:opacity-40 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2"
          data-testid="bar-take-order-launch"
        >
          <ShoppingCart className="w-4 h-4" /> Start Order
        </Button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="bar-take-order-grid">
        {tables.map(t => {
          const selected = picked === t.qr_code;
          const occupied = t.status === 'occupied';
          const reserved = t.status === 'reserved';
          const borderCls = selected
            ? 'border-gold ring-2 ring-gold/40'
            : occupied
              ? 'border-amber-500/40 hover:border-amber-300'
              : reserved
                ? 'border-blue-accent/40 hover:border-blue-accent'
                : 'border-emerald-500/40 hover:border-emerald-300';
          const badgeCls = occupied
            ? 'bg-amber-500/20 text-amber-300'
            : reserved
              ? 'bg-blue-accent/20 text-blue-accent'
              : 'bg-emerald-500/20 text-emerald-300';
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setPicked(t.qr_code)}
              className={`flex flex-col items-center gap-1.5 p-4 rounded-sm border bg-white/[0.03] transition-all ${borderCls}`}
              data-testid={`bar-take-order-table-${t.number}`}
            >
              <span className="text-gold font-serif text-3xl font-semibold leading-none">{tableLabel(t.number)}</span>
              <span className={`px-1.5 py-0.5 rounded-sm font-sans text-[10px] font-bold uppercase tracking-[0.15em] ${badgeCls}`}>{t.status}</span>
              <span className="text-[10px] text-zinc-500 font-sans">{t.capacity} seats</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ============================================================
   TABLES TAB
   Same rich grid as the Serving panel + the "Force Clear" power
   (which the backend now grants to bartender, manager, director).
   ============================================================ */
function BarTablesTab({ tables, avail, occ, resv, cleaning, refresh, userRole, onTabSettled }) {
  const canForce = ['bartender', 'manager', 'director'].includes(userRole);
  // Drill-down state — clicking a table card opens this modal with the
  // full list of active tabs on that table.
  const [drillTable, setDrillTable] = useState(null);

  const setStatus = async (t, status) => {
    try {
      await api.patch(`/tables/${t.id}/status`, { status });
      toast.success(`${tableLabel(t.number)} → ${status}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update table', { duration: 5000 });
    }
  };

  const clearTable = async (t) => {
    if (!window.confirm(`Mark ${tableLabel(t.number)} as cleared?`)) return;
    try {
      await api.post(`/tables/${t.id}/clear`);
      toast.success(`${tableLabel(t.number)} → available`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed', { duration: 5000 });
    }
  };

  // Force-clear voids any unpaid orders + closes any open tab. Two-step
  // confirm because the audit log shows who did it.
  const forceClear = async (t) => {
    const first = window.prompt(
      `FORCE-CLEAR ${tableLabel(t.number)}\n\n` +
      `This will VOID any unpaid orders + CLOSE any open tab on this table.\n` +
      `Only use this when you've already collected payment OUTSIDE the app\n` +
      `(cash in drawer, EFTPOS slip captured) but the order was never marked paid.\n\n` +
      `Type the table label below to confirm:`
    );
    if (!first || first.trim().toUpperCase() !== String(tableLabel(t.number)).toUpperCase()) {
      if (first !== null) toast.message('Force clear cancelled — label did not match.');
      return;
    }
    const reason = window.prompt('Reason for force-clearing? (audit log)') || 'no reason given';
    try {
      const { data } = await api.patch(`/tables/${t.id}/status`, {
        status: 'available',
        force: true,
        force_reason: reason,
      });
      const actions = data?.forced_actions?.map(a => a.action).join(', ') || 'cleared';
      toast.success(`${tableLabel(t.number)} force-cleared (${actions})`, { duration: 5000 });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Force-clear failed', { duration: 6000 });
    }
  };

  return (
    <section data-testid="bar-tables-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <h2 className="text-sm sm:text-base font-serif text-white uppercase tracking-[0.18em]">Tables</h2>
          <p className="text-[10px] text-zinc-500 font-sans">
            <span className="text-emerald-400">{avail} avail</span>
            {' · '}<span className="text-amber-300">{occ} occ</span>
            {' · '}<span className="text-orange-300">{cleaning || 0} cleaning</span>
            {' · '}<span className="text-blue-accent">{resv} resv</span>
            {canForce && <span className="ml-2 text-red-300/80">· Force-clear available</span>}
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-sans hidden sm:inline">
          Tap a card to view tabs · live every 8s
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="bar-tables-grid">
        {tables.map(t => {
          const cardCls = t.status === 'occupied'
            ? 'border-amber-500/35 hover:border-amber-400'
            : t.status === 'cleaning'
              ? 'border-orange-500/40 hover:border-orange-400'
              : t.status === 'reserved'
                ? 'border-blue-accent/30 hover:border-blue-accent'
                : 'border-emerald-500/30 hover:border-emerald-400';
          const badgeCls = t.status === 'occupied'
            ? 'bg-amber-500/15 text-amber-300'
            : t.status === 'cleaning'
              ? 'bg-orange-500/15 text-orange-300'
              : t.status === 'reserved'
                ? 'bg-blue-accent/15 text-blue-accent'
                : 'bg-emerald-500/15 text-emerald-300';
          const tabCount = t.active_tab_count || 0;
          const tabsTotal = Number(t.active_tabs_total || 0);
          const occupiedSince = t.occupied_since;
          return (
            <div
              key={t.id}
              className={`bg-white/[0.03] border rounded-sm p-3 flex flex-col items-center text-center transition-all cursor-pointer ${cardCls}`}
              onClick={() => setDrillTable(t)}
              data-testid={`bar-table-card-${t.number}`}
            >
              <p className="text-gold font-serif text-2xl font-semibold leading-none mb-1.5">{tableLabel(t.number)}</p>
              <span className={`px-1.5 py-0.5 rounded-sm font-sans text-[9px] font-bold uppercase tracking-[0.15em] ${badgeCls} mb-2`} data-testid={`bar-table-status-${t.number}`}>
                {t.status}
              </span>
              {/* Per-table stats — only meaningful when the table is occupied */}
              {tabCount > 0 ? (
                <div className="w-full grid grid-cols-2 gap-1 mb-2 text-[10px] font-sans">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm py-1 px-1.5" data-testid={`bar-table-tabs-count-${t.number}`}>
                    <p className="text-amber-300 font-bold text-sm leading-none">{tabCount}</p>
                    <p className="text-amber-200/60 uppercase tracking-[0.12em] text-[8px]">tabs</p>
                  </div>
                  <div className="bg-gold/10 border border-gold/30 rounded-sm py-1 px-1.5" data-testid={`bar-table-tabs-total-${t.number}`}>
                    <p className="text-gold font-bold text-sm leading-none">${tabsTotal.toFixed(0)}</p>
                    <p className="text-gold/60 uppercase tracking-[0.12em] text-[8px]">unpaid</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-1 text-[10px] text-zinc-500 font-sans mb-2">
                  <Users className="w-3 h-3" /> {t.capacity}
                  <span className="mx-1">·</span>
                  <QrCode className="w-3 h-3" />
                  <span className="font-mono">{t.qr_code.replace(/^MAJNU/, '')}</span>
                </div>
              )}
              {occupiedSince && tabCount > 0 && (
                <p className="text-[10px] text-zinc-500 font-sans mb-2" data-testid={`bar-table-occupied-since-${t.number}`}>
                  <ElapsedSince ts={occupiedSince} />
                </p>
              )}
              <div className="w-full grid grid-cols-1 gap-1 mt-auto" onClick={(e) => e.stopPropagation()}>
                {t.status !== 'available' && (
                  <button
                    onClick={() => clearTable(t)}
                    className="w-full px-2 py-1 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-sm font-sans text-[10px] uppercase tracking-[0.12em] font-semibold flex items-center justify-center gap-1"
                    data-testid={`bar-mark-available-${t.number}`}
                  >
                    <DoorOpen className="w-3 h-3" /> Available
                  </button>
                )}
                {t.status !== 'occupied' && (
                  <button
                    onClick={() => setStatus(t, 'occupied')}
                    className="w-full px-2 py-1 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30 rounded-sm font-sans text-[10px] uppercase tracking-[0.12em] font-semibold"
                    data-testid={`bar-mark-occupied-${t.number}`}
                  >
                    Occupied
                  </button>
                )}
                {t.status !== 'cleaning' && (
                  <button
                    onClick={() => setStatus(t, 'cleaning')}
                    className="w-full px-2 py-1 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 border border-orange-500/30 rounded-sm font-sans text-[10px] uppercase tracking-[0.12em] font-semibold"
                    data-testid={`bar-mark-cleaning-${t.number}`}
                  >
                    Cleaning
                  </button>
                )}
                {canForce && t.status !== 'available' && (
                  <button
                    onClick={() => forceClear(t)}
                    className="w-full px-2 py-1 bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/30 rounded-sm font-sans text-[10px] uppercase tracking-[0.12em] font-semibold flex items-center justify-center gap-1"
                    data-testid={`bar-force-clear-${t.number}`}
                  >
                    <AlertTriangle className="w-3 h-3" /> Force Clear
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {drillTable && (
        <BarTableTabsDrawer
          table={drillTable}
          onClose={() => setDrillTable(null)}
          refresh={refresh}
          onTabSettled={onTabSettled}
          userRole={userRole}
        />
      )}
    </section>
  );
}

/* ============================================================
   BAR TABLE TABS DRAWER
   Click a table card → see every active tab on that table with
   running totals + quick actions (View · Settle · Force Close).
   Manager-only Merge button arrives in Batch 2.
   ============================================================ */
function ElapsedSince({ ts }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(i);
  }, []);
  if (!ts) return null;
  const ms = now - new Date(ts).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return <span data-testid="elapsed-since">{h > 0 ? `${h}h ${m}m` : `${m}m`}</span>;
}

function BarTableTabsDrawer({ table, onClose, refresh, onTabSettled, userRole }) {
  const canMerge = ['manager', 'director'].includes(userRole);
  const [busyId, setBusyId] = useState('');
  const [mergeSourceId, setMergeSourceId] = useState(''); // tab being merged FROM
  const [latestTable, setLatestTable] = useState(table);
  const fetchLatest = useCallback(async () => {
    try {
      const { data } = await api.get('/tables/overview');
      const t = (data || []).find(x => x.id === table.id);
      if (t) setLatestTable(t);
    } catch { /* keep current */ }
  }, [table.id]);
  useEffect(() => {
    fetchLatest();
    const i = setInterval(fetchLatest, 8000);
    return () => clearInterval(i);
  }, [fetchLatest]);

  const tabs = latestTable.active_tabs || [];
  const totalAll = tabs.reduce((s, t) => s + Number(t.current_total || 0), 0);

  const settleCash = async (tab) => {
    if (!window.confirm(
      `Settle ${tab.customer_name || 'guest'}'s tab on T${latestTable.number} for $${Number(tab.current_total || 0).toFixed(2)} — CASH?\n\n` +
      `Make sure you've collected the cash before confirming.`
    )) return;
    setBusyId(tab.id);
    try {
      // Use the tab receipt to pick a sibling order — the existing
      // settle endpoint takes any order_id on the tab and closes the
      // whole tab.
      const { data: receipt } = await api.get(`/tabs/${tab.id}/receipt`);
      const orderId = receipt?.orders?.[0]?.id;
      if (!orderId) throw new Error('No order found on this tab');
      await api.post(`/tabs/by-order/${orderId}/settle?settlement_method=cash`);
      toast.success(`Tab settled — $${Number(tab.current_total || 0).toFixed(2)} cash`);
      refresh();
      await fetchLatest();
      onTabSettled?.({ id: latestTable.id, number: latestTable.number });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Settle failed', { duration: 5000 });
    } finally {
      setBusyId('');
    }
  };

  // Merge a SECONDARY tab into a PRIMARY tab. Two-step: tap "Merge" on the
  // tab that should be folded in, then tap the destination tab. Reason is
  // required (audit log).
  const beginMerge = (tab) => {
    if (tabs.length < 2) {
      toast.message('Need at least 2 active tabs on the table to merge.');
      return;
    }
    setMergeSourceId(tab.id);
    toast.message(`Pick the destination tab to merge "${tab.customer_name || 'guest'}" into.`);
  };

  const confirmMerge = async (destinationTab) => {
    const source = tabs.find(t => t.id === mergeSourceId);
    if (!source || source.id === destinationTab.id) {
      setMergeSourceId('');
      return;
    }
    const reason = window.prompt(
      `Merge tab "${source.customer_name || 'guest'}" ($${Number(source.current_total || 0).toFixed(2)}) ` +
      `INTO tab "${destinationTab.customer_name || 'guest'}" ($${Number(destinationTab.current_total || 0).toFixed(2)})?\n\n` +
      `Reason (required for audit log):`,
      'Combining bills at customer request',
    );
    if (!reason || !reason.trim()) {
      toast.message('Merge cancelled — a reason is required.');
      setMergeSourceId('');
      return;
    }
    setBusyId(source.id);
    try {
      const { data } = await api.post(`/tabs/${destinationTab.id}/merge`, {
        secondary_tab_id: source.id,
        reason: reason.trim(),
      });
      toast.success(`Merged · $${Number(data.new_primary_total || 0).toFixed(2)} unpaid on ${destinationTab.customer_name || 'destination'}`);
      setMergeSourceId('');
      refresh();
      await fetchLatest();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Merge failed', { duration: 5000 });
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="bar-tabs-drawer">
      <div
        className="bg-bg-primary border border-gold/20 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-serif text-2xl text-gold leading-none" data-testid="drawer-title">{tableLabel(latestTable.number)}</h3>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans mt-1">
              {tabs.length} active tab{tabs.length === 1 ? '' : 's'} · ${totalAll.toFixed(2)} unpaid
              {latestTable.occupied_since && <> · <ElapsedSince ts={latestTable.occupied_since} /> occupied</>}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white" data-testid="close-drawer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {mergeSourceId && (
            <div className="px-3 py-2 bg-blue-accent/10 border border-blue-accent/30 rounded-sm text-blue-accent font-sans text-xs flex items-center justify-between" data-testid="merge-picker-active">
              <span>📋 Pick the destination tab below to merge into…</span>
              <button onClick={() => setMergeSourceId('')} className="text-blue-accent hover:text-white uppercase tracking-[0.15em]" data-testid="merge-cancel">Cancel</button>
            </div>
          )}
          {tabs.length === 0 ? (
            <p className="text-center text-zinc-500 font-sans py-10" data-testid="no-tabs">No active tabs on this table.</p>
          ) : tabs.map((t, idx) => {
            const isMergeSource = mergeSourceId === t.id;
            const isPickerActive = !!mergeSourceId && !isMergeSource;
            return (
            <div
              key={t.id}
              className={`bg-white/[0.03] border rounded-sm p-4 transition-all ${isMergeSource ? 'border-blue-accent/60 bg-blue-accent/[0.06]' : isPickerActive ? 'border-emerald-500/40 hover:border-emerald-300 cursor-pointer' : 'border-white/10'}`}
              onClick={isPickerActive ? () => confirmMerge(t) : undefined}
              data-testid={`tab-row-${t.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="font-sans text-white text-sm font-semibold">
                    Tab {idx + 1} · {t.customer_name || (t.is_guest ? 'Guest' : 'Member')}
                    {isMergeSource && <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-blue-accent">merging</span>}
                    {isPickerActive && <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-emerald-300">tap to merge here</span>}
                  </p>
                  <p className="text-[10px] text-zinc-500 font-sans">
                    {t.is_guest ? 'Walk-in' : 'Member'} · opened <ElapsedSince ts={t.created_at} /> ago
                    {t.deposit_method && <> · deposit {t.deposit_method}</>}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-gold text-xl leading-none">${Number(t.current_total || 0).toFixed(2)}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-amber-300 font-sans mt-1">Unpaid</p>
                </div>
              </div>
              {!isPickerActive && !isMergeSource && (
                <div className={`grid ${canMerge && tabs.length >= 2 ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
                  <button
                    onClick={(e) => { e.stopPropagation(); settleCash(t); }}
                    disabled={busyId === t.id}
                    className="px-3 py-2 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-sm font-sans text-xs uppercase tracking-[0.12em] font-semibold disabled:opacity-50"
                    data-testid={`tab-settle-cash-${t.id}`}
                  >
                    Settle · Cash
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onClose(); window.location.href = `/dashboard/orders`; }}
                    className="px-3 py-2 bg-blue-accent/10 text-blue-accent hover:bg-blue-accent/20 border border-blue-accent/30 rounded-sm font-sans text-xs uppercase tracking-[0.12em] font-semibold"
                    data-testid={`tab-open-payments-${t.id}`}
                  >
                    Card / Apple Pay
                  </button>
                  {canMerge && tabs.length >= 2 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); beginMerge(t); }}
                      className="px-3 py-2 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30 rounded-sm font-sans text-xs uppercase tracking-[0.12em] font-semibold"
                      data-testid={`tab-merge-${t.id}`}
                    >
                      Merge
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between text-[11px] font-sans text-zinc-500">
          <span>Live · refreshes every 8s</span>
          <button onClick={onClose} className="text-zinc-300 hover:text-white uppercase tracking-[0.15em] text-[11px]">Close</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PAYMENTS TAB
   Groups active unpaid orders by tab (when applicable) and shows
   running totals + per-row Settle controls. Cash settles inline
   via /tabs/by-order/{id}/settle?settlement_method=cash. For card /
   Apple Pay / EFTPOS we deep-link to Live Orders which already
   owns the full Square / split-payment dialog.
   ============================================================ */
function BarPaymentsTab({ tabPayments, directPayments, refresh, navigate, onTabSettled }) {
  const [settling, setSettling] = useState('');

  // Group tab orders by tab_id so the bartender sees one row per customer
  // with their running balance (matches how Live Orders shows it).
  // table_id is captured so the post-settle prompt can flip it without
  // needing a second fetch.
  const tabGroups = {};
  for (const o of tabPayments) {
    if (!o.tab_id) continue;
    if (!tabGroups[o.tab_id]) tabGroups[o.tab_id] = {
      tab_id: o.tab_id,
      table_id: o.table_id,
      table: o.table_number,
      customer: o.customer_name,
      orders: [],
      total: 0,
    };
    tabGroups[o.tab_id].orders.push(o);
    tabGroups[o.tab_id].total += Number(o.total || 0);
  }
  // Roll up each tab's discount state — "mixed" if siblings disagree, the
  // common kind ("cash_10" / "friends_20" / null) otherwise. Drives which
  // pill is highlighted. We also track whether ANY sibling carries a
  // pending discount so the row can show the amber PENDING badge.
  for (const g of Object.values(tabGroups)) {
    const kinds = new Set(g.orders.map(o => o.discount_kind || null));
    g.discount_kind = kinds.size === 1 ? [...kinds][0] : 'mixed';
    g.discount_pending = g.orders.some(o => o.discount_status === 'pending');
  }
  const tabRows = Object.values(tabGroups).sort((a, b) => String(a.table).localeCompare(String(b.table), undefined, { numeric: true }));

  // Apply (or clear) a quick 10% / 20% staff discount across every unpaid
  // order on a tab. Loops through siblings so we don't have to invent a
  // tab-level endpoint — the per-order /discount route stamps the right
  // kind + label and the existing settle path picks up the new totals.
  // Bartender-applied 20% off is held as pending until a manager
  // approves it on the Discount Report.
  const applyTabDiscount = async (row, kind) => {
    setSettling(`disc-${row.tab_id}`);
    try {
      const results = await Promise.all(
        row.orders.map(o => api.post(`/orders/${o.id}/discount`, { kind }))
      );
      const anyPending = results.some(r => r.data?.discount_status === 'pending');
      if (kind === 'none') {
        toast.success(`Discount cleared on Table ${row.table}`);
      } else if (anyPending) {
        toast.info(`20% off submitted for manager approval on Table ${row.table}`, { duration: 4500 });
      } else {
        toast.success(`${kind === 'cash_10' ? '10% off' : '20% off'} applied to Table ${row.table}`);
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setSettling('');
    }
  };

  const applyOrderDiscount = async (o, kind) => {
    setSettling(`disc-${o.id}`);
    try {
      const { data } = await api.post(`/orders/${o.id}/discount`, { kind });
      if (kind === 'none') {
        toast.success('Discount cleared');
      } else if (data?.discount_status === 'pending') {
        toast.info('20% off submitted for manager approval', { duration: 4500 });
      } else {
        toast.success(`${kind === 'cash_10' ? '10% off' : '20% off'} applied`);
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setSettling('');
    }
  };

  const settleTabCash = async (row) => {
    if (!window.confirm(
      `Settle ${row.customer || 'guest'} on Table ${row.table} for $${row.total.toFixed(2)} — CASH?\n\n` +
      `This closes the whole tab. Make sure you've collected the cash before confirming.`
    )) return;
    setSettling(row.tab_id);
    try {
      // Settle by tab: pick one of the orders on the tab — backend will
      // close the whole tab when settlement_method is cash.
      const orderId = row.orders[0].id;
      await api.post(`/tabs/by-order/${orderId}/settle?settlement_method=cash`);
      toast.success(`Tab settled — $${row.total.toFixed(2)} cash collected from ${row.customer || 'guest'}`);
      refresh();
      // Open the post-settle prompt so the bartender can decide whether
      // to free the table or keep it occupied.
      if (row.table_id) onTabSettled?.({ id: row.table_id, number: row.table });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Settle failed', { duration: 5000 });
    } finally {
      setSettling('');
    }
  };

  const settleDirectCash = async (o) => {
    if (!window.confirm(`Settle ${o.customer_name || 'guest'} (T${o.table_number}) for $${Number(o.total).toFixed(2)} — CASH?`)) return;
    setSettling(o.id);
    try {
      // Mark as paid by cash (existing endpoint also used by Live Orders' Cash pill).
      await api.post(`/payments/cash-received/${o.id}`);
      toast.success(`Order #${o.id.slice(0, 6)} settled — cash collected`);
      refresh();
      // Same post-settle prompt for direct (non-tab) cash orders — useful
      // when a one-shot walk-up customer has paid + is now leaving.
      if (o.table_id) onTabSettled?.({ id: o.table_id, number: o.table_number });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Settle failed', { duration: 5000 });
    } finally {
      setSettling('');
    }
  };

  const goToFullPaymentFlow = () => {
    toast.message('Opening Live Orders for full Square / Apple Pay / EFTPOS flow…');
    navigate('/dashboard/orders');
  };

  if (tabRows.length === 0 && directPayments.length === 0) {
    return (
      <section data-testid="bar-payments-empty">
        <StaleTabsAlert />
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <CreditCard className="w-12 h-12 text-zinc-700 mb-4" />
          <p className="text-zinc-500 font-sans">No pending payments — everyone's settled.</p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="bar-payments-section">
      <StaleTabsAlert />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-sm sm:text-base font-serif text-white uppercase tracking-[0.18em]">Pending Payments</h2>
          <p className="text-[10px] sm:text-xs text-zinc-500 font-sans mt-1">
            Cash settles inline. For Card / Apple Pay / EFTPOS / split payments → tap "Full Payment Flow".
          </p>
        </div>
        <Button
          onClick={goToFullPaymentFlow}
          className="h-9 bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold"
          data-testid="bar-payments-full-flow"
        >
          Full Payment Flow ↗
        </Button>
      </div>

      {tabRows.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-sans text-amber-300 uppercase tracking-[0.18em] mb-2 font-semibold">Open Tabs ({tabRows.length})</h3>
          <div className="space-y-2" data-testid="bar-payments-tab-rows">
            {tabRows.map(row => (
              <div key={row.tab_id} className="bg-white/[0.03] border border-amber-500/20 rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid={`bar-payment-tab-${row.tab_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gold font-serif text-xl font-semibold">{tableLabel(row.table)}</span>
                    <span className="text-white font-sans text-sm truncate">{row.customer || 'guest'}</span>
                    <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono text-[10px]">{row.orders.length} order{row.orders.length > 1 ? 's' : ''}</Badge>
                  </div>
                  <p className="text-amber-300 font-mono text-base font-semibold mt-0.5">${row.total.toFixed(2)}</p>
                </div>
                <DiscountPills
                  currentKind={row.discount_kind}
                  pending={row.discount_pending}
                  disabled={settling === `disc-${row.tab_id}`}
                  onPick={(kind) => applyTabDiscount(row, kind)}
                  testIdPrefix={`bar-payment-tab-discount-${row.tab_id}`}
                />
                <Button
                  onClick={() => settleTabCash(row)}
                  disabled={settling === row.tab_id}
                  className="h-9 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold disabled:opacity-50"
                  data-testid={`bar-payment-cash-${row.tab_id}`}
                >
                  {settling === row.tab_id ? <X className="w-3.5 h-3.5" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Cash · ${row.total.toFixed(2)}</>}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {directPayments.length > 0 && (
        <div>
          <h3 className="text-xs font-sans text-zinc-400 uppercase tracking-[0.18em] mb-2 font-semibold">Direct Orders ({directPayments.length})</h3>
          <div className="space-y-2" data-testid="bar-payments-direct-rows">
            {directPayments.map(o => (
              <div key={o.id} className="bg-white/[0.03] border border-white/10 rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid={`bar-payment-direct-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gold font-serif text-lg font-semibold">{tableLabel(o.table_number)}</span>
                    <span className="text-white font-sans text-sm truncate">{o.customer_name || 'guest'}</span>
                    <Badge className="bg-white/5 text-zinc-300 border-0 font-mono text-[10px]">#{o.id.slice(0, 6)}</Badge>
                    <Badge className="bg-white/5 text-zinc-400 border-0 font-sans text-[10px] uppercase tracking-[0.15em]">{o.payment_method}</Badge>
                  </div>
                  <p className="text-zinc-200 font-mono text-base font-semibold mt-0.5">${Number(o.total).toFixed(2)}</p>
                </div>
                <DiscountPills
                  currentKind={o.discount_kind || null}
                  pending={o.discount_status === 'pending'}
                  disabled={settling === `disc-${o.id}`}
                  onPick={(kind) => applyOrderDiscount(o, kind)}
                  testIdPrefix={`bar-payment-direct-discount-${o.id}`}
                />
                {o.payment_method === 'cash' ? (
                  <Button
                    onClick={() => settleDirectCash(o)}
                    disabled={settling === o.id}
                    className="h-9 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold disabled:opacity-50"
                    data-testid={`bar-payment-direct-cash-${o.id}`}
                  >
                    {settling === o.id ? <X className="w-3.5 h-3.5" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Cash collected</>}
                  </Button>
                ) : (
                  <Button
                    onClick={goToFullPaymentFlow}
                    className="h-9 bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold"
                    data-testid={`bar-payment-direct-flow-${o.id}`}
                  >
                    Process ↗
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* ============================================================
   DISCOUNT PILLS — segmented 0% / 10% / 20% toggle
   used inline on every Bar Panel payments row. Highlights the
   current state; clicking re-posts /orders/{id}/discount via
   the parent handler so the row total refreshes and the Cash /
   Process button picks up the new amount automatically.
   ============================================================ */
function DiscountPills({ currentKind, pending, disabled, onPick, testIdPrefix }) {
  const opts = [
    { kind: 'none',       label: '0%',    cls: 'text-zinc-400 border-white/15' },
    { kind: 'cash_10',    label: '10% off', cls: 'text-emerald-300 border-emerald-500/40' },
    { kind: 'friends_20', label: '20% off', cls: 'text-amber-300 border-amber-500/40' },
  ];
  // "mixed" = sibling orders on a tab disagree → show none-selected so the
  // bartender can re-apply uniformly.
  const active = currentKind === 'mixed' ? null : (currentKind || 'none');
  return (
    <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-group`}>
      {opts.map(o => {
        const isActive = active === o.kind;
        const isPending = isActive && pending && o.kind === 'friends_20';
        return (
          <button
            key={o.kind}
            type="button"
            disabled={disabled || isActive}
            onClick={() => onPick(o.kind)}
            className={`h-8 px-2 rounded-sm border font-sans text-[10px] uppercase tracking-[0.15em] font-semibold transition disabled:opacity-50 ${
              isActive
                ? isPending
                  ? `bg-amber-500/[0.08] text-amber-300 border-amber-500/60 ring-1 ring-amber-400/60 animate-pulse`
                  : `bg-white/[0.08] ${o.cls} ring-1 ring-gold/40`
                : `bg-transparent ${o.cls} hover:bg-white/[0.05]`
            }`}
            data-testid={`${testIdPrefix}-${o.kind}`}
            title={
              isPending
                ? 'Waiting for manager approval'
                : (o.kind === 'none' ? 'Remove discount' : `Apply ${o.label}`)
            }
          >
            {isPending ? '20% PENDING' : o.label}
          </button>
        );
      })}
      {currentKind === 'mixed' && (
        <span className="text-[10px] text-amber-400 font-sans ml-1" data-testid={`${testIdPrefix}-mixed-warning`}>mixed</span>
      )}
    </div>
  );
}

/* ============================================================
   SERVICE TAB
   Show all PENDING service requests (water / bill / napkins / coal
   / payment / etc.) with Accept + Complete buttons. The audio alert
   + floating tray (ServiceRequestListener) keeps running on top of
   this — this tab is for the bartender to see ALL active requests
   and clear them in batches.
   ============================================================ */
function BarServiceTab({ requests, refresh }) {
  const [busy, setBusy] = useState('');

  const accept = async (req) => {
    setBusy(req.id);
    try {
      await api.put(`/service-requests/${req.id}/accept`);
      toast.success(`Accepted Table ${req.table_number}`);
      refresh();
    } catch (err) {
      if (err.response?.status === 409) toast.info('Already accepted by another staff');
      else toast.error(err.response?.data?.detail || 'Could not accept');
      refresh();
    } finally { setBusy(''); }
  };

  const complete = async (req) => {
    setBusy(req.id);
    try {
      await api.put(`/service-requests/${req.id}/complete`);
      toast.success(`Completed Table ${req.table_number}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete');
      refresh();
    } finally { setBusy(''); }
  };

  const TYPE_LABELS = {
    payment_eftpos: 'Collect Payment via EFTPOS',
    payment: 'Payment assistance',
    help: 'Service help',
    water: 'Refill water',
    bill: 'Bring the bill',
    napkins: 'Need napkins',
    coal: 'Change shisha coal',
  };

  if (requests.length === 0) {
    return (
      <section data-testid="bar-service-empty" className="flex flex-col items-center justify-center py-20 text-center">
        <BellRing className="w-12 h-12 text-zinc-700 mb-4" />
        <p className="text-zinc-500 font-sans">No active service requests — guests are happy.</p>
      </section>
    );
  }

  return (
    <section data-testid="bar-service-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-sm sm:text-base font-serif text-white uppercase tracking-[0.18em]">Active Service Requests</h2>
          <p className="text-[10px] sm:text-xs text-zinc-500 font-sans mt-1">
            First to Accept stops the beep for everyone. Mark Complete once you've handled it.
          </p>
        </div>
      </div>
      <div className="space-y-2" data-testid="bar-service-list">
        {requests.map(req => {
          const friendly = TYPE_LABELS[req.request_type] || req.request_type;
          const accepted = !!req.accepted_by;
          const accentCls = req.request_type === 'payment_eftpos'
            ? 'border-blue-accent/40 bg-blue-accent/[0.06]'
            : accepted
              ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
              : 'border-red-500/30 bg-red-500/[0.05]';
          return (
            <div key={req.id} className={`border rounded-sm p-3 flex items-center gap-3 flex-wrap ${accentCls}`} data-testid={`bar-service-row-${req.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gold font-serif text-lg font-semibold">{tableLabel(req.table_number)}</span>
                  <span className="text-white font-sans text-sm">{friendly}</span>
                  {accepted && (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-sans text-[10px] uppercase tracking-[0.12em]">
                      Accepted · {req.accepted_by_name || 'staff'}
                    </Badge>
                  )}
                </div>
                {req.customer_name && <p className="text-zinc-400 font-sans text-xs mt-0.5">{req.customer_name}</p>}
                {req.note && <p className="text-zinc-500 text-[11px] font-sans mt-0.5 italic">"{req.note}"</p>}
              </div>
              {!accepted ? (
                <Button
                  onClick={() => accept(req)}
                  disabled={busy === req.id}
                  className="h-9 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold disabled:opacity-50"
                  data-testid={`bar-service-accept-${req.id}`}
                >
                  <Check className="w-3.5 h-3.5 mr-1" /> Accept
                </Button>
              ) : (
                <Button
                  onClick={() => complete(req)}
                  disabled={busy === req.id}
                  className="h-9 bg-blue-accent/15 text-blue-accent hover:bg-blue-accent/25 border border-blue-accent/30 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold disabled:opacity-50"
                  data-testid={`bar-service-complete-${req.id}`}
                >
                  <Check className="w-3.5 h-3.5 mr-1" /> Complete
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
